import { useState, useEffect } from 'react';
import fields from '~components/OrderForm/constants';
import { getPromoNumberOfMonthes } from '~utils/promoCode';

export const useAnalytics = () => {
  const [analyticsLoaded, setIsAnalyticsLoaded] = useState(!!window.analytics && !!window.TempoEvents);
  let TempoEvents = window?.TempoEvents;
  const handleDocLoaded = () => {
    setIsAnalyticsLoaded(!!window.analytics && !!window.TempoEvents);
    if (typeof window !== 'undefined' && !!window.TempoEvents) {
      TempoEvents = window?.TempoEvents;
      TempoEvents.init({
        appName: 'Members',
        APIBaseUrl: process.env.REACT_APP_TEMPO_API_URL || 'https://api.trainwithpivot.com',
        APIToken: "bWVtYmVyc0B0ZW1wby5jb206bWVtYmVycw=="
      });
      TempoEvents.page('pageview', {});
    }
  };
  useEffect(() => {
    window.addEventListener('load', handleDocLoaded);
    return () => {
      window.removeEventListener('load', handleDocLoaded);
    };
  }, []);

  const _analytics = () => {
    if (analyticsLoaded) {
      return window?.analytics;
    }
  };

  // Save the form data in session storage
  const saveFormData = (formId, formData) => {
    try {
      sessionStorage.setItem(String(formId), JSON.stringify(formData));
    } catch (e) {}
  };

  const getFormData = (formId) => {
    try {
      return JSON.parse(sessionStorage.getItem(String(formId)));
    } catch (e) {}
  };

  const trackRegisterPageViewed = (promoGiftCode, orderId, extraProps) => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Page Viewed', {
        promoGiftCode: promoGiftCode || '',
        orderId: orderId || '',
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();

      analytics.track('Register Page Viewed', {
        promoGiftCode: promoGiftCode || '',
        orderId: orderId || '',
        ...extraProps,
        validationId,
      });
    }
  };

  const trackRegisterPageSubmitTapped = (formData, extraProps) => {
    let validationId = '';
    const address = formData[fields.billingAddress];
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Page Submit Tapped', {
        firstName: formData[fields.firstName] || '',
        lastName: formData[fields.lastName] || '',
        email: formData[fields.email] || '',
        promoGiftCode: formData[fields.subscriptionCreditCode] || '',
        paymentCity: address?.city || '',
        paymentProvince: address?.province || '',
        paymentZipCode: address?.postal_code || '',
        termsOfServiceChecked: !!formData[fields.hasAcceptedTerms],
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Register Page Submit Tapped', {
        firstName: formData[fields.firstName] || '',
        lastName: formData[fields.lastName] || '',
        email: formData[fields.email] || '',
        promoGiftCode: formData[fields.subscriptionCreditCode] || '',
        paymentCity: address?.city || '',
        paymentProvince: address?.province || '',
        paymentZipCode: address?.postal_code || '',
        termsOfServiceChecked: !!formData[fields.hasAcceptedTerms],
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };
  const trackRegisterSuccessfulPageViewed = (formData, extraProps) => {
    let validationId = '';
    const promoGiftCode = formData[fields.subscriptionCreditCode] || '';
    const numberOfGiftedMonths = getPromoNumberOfMonthes(promoGiftCode);
    let successMessage = 'Your membership will start once you receive and activate your Tempo.';
    if (numberOfGiftedMonths > 0) {
      successMessage += `Free gifted months will be applied when you activate your device.`;
    }
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Success Page Viewed', {
        email: formData[fields.email] || '',
        successMessage,
        promoGiftCode,
        ...(promoGiftCode && { isPromoGiftCodeSuccessful: true }),
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();

      analytics.track('Register Success Page Viewed', {
        email: formData[fields.email] || '',
        successMessage,
        promoGiftCode,
        ...(promoGiftCode && { isPromoGiftCodeSuccessful: true }),
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };

  const trackRegisterSuccessfulPageOpenTempoAppTapped = (formData, extraProps) => {
    let validationId = '';
    const promoGiftCode = formData[fields.subscriptionCreditCode] || '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Successful Page Open Tempo App Tapped', {
        email: formData[fields.email] || '',
        promoGiftCode,
        ...(promoGiftCode && { isPromoGiftCodeSuccessful: true }),
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Register Successful Page Open Tempo App Tapped', {
        email: formData[fields.email] || '',
        promoGiftCode,
        ...(promoGiftCode && { isPromoGiftCodeSuccessful: true }),
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };

  const trackExistingSubscriptionPageViewed = (formData, extraProps) => {
    let validationId = '';
    const promoGiftCode = formData[fields.subscriptionCreditCode] || '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Existing Subscription Page Viewed', {
        email: formData[fields.email] || '',
        errorMessage:
          'We couldn’t activate your membership. There’s already an active subscription tied to this email. Contact support to activate your Tempo Move or Studio.',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Register Existing Subscription Page Viewed', {
        email: formData[fields.email] || '',
        errorMessage:
          'We couldn’t activate your membership. There’s already an active subscription tied to this email. Contact support to activate your Tempo Move or Studio.',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };
  const trackExistingSubscriptionPageContactSupportTapped = (formData, extraProps) => {
    let validationId = '';
    const promoGiftCode = formData[fields.subscriptionCreditCode] || '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Existing Subscription Page Contact Support Tapped', {
        email: formData[fields.email] || '',
        errorMessage:
          'We couldn’t activate your membership. There’s already an active subscription tied to this email. Contact support to activate your Tempo Move or Studio.',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Register Existing Subscription Page Contact Support Tapped', {
        email: formData[fields.email] || '',
        errorMessage:
          'We couldn’t activate your membership. There’s already an active subscription tied to this email. Contact support to activate your Tempo Move or Studio.',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };
  const trackRegisterPaymentFailure = (formData, errorMessage, extraProps) => {
    let validationId = '';
    const promoGiftCode = formData[fields.subscriptionCreditCode] || '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Register Payment Failure', {
        email: formData[fields.email] || '',
        errorMessage: errorMessage || '',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();

      analytics.track('Register Payment Failure', {
        email: formData[fields.email] || '',
        errorMessage: errorMessage || '',
        promoGiftCode,
        purchasedFrom: formData[fields.purchaseSource] || '',
        ...(formData[fields.orderId] && { orderId: formData[fields.orderId] }),
        ...(formData[fields.orderType] && { product: formData[fields.orderType] }),
        ...extraProps,
        validationId,
      });
    }
  };
  const trackSubscriptionInvitationPageViewed = () => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Subscription Invitation Page Viewed');
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Subscription Invitation Page Viewed', { validationId });
    }
  };
  const trackSubscriptionInvitationJoinFamilyTapped = (email, inviteCode, extraProps) => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Subscription Invitation Page Join Family Tapped', {
        email,
        inviteCode,
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Subscription Invitation Page Join Family Tapped', {
        email,
        inviteCode,
        ...extraProps,
        validationId,
      });
    }
  };
  const trackSubscriptionInvitationConfirmationPageViewed = () => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Subscription Invitation Confirmation Page Viewed');
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Subscription Invitation Confirmation Page Viewed', { validationId });
    }
  };
  const trackSubscriptionInvitationConfirmationPageOpenTempoAppTapped = (email, inviteCode, extraProps) => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Subscription Invitation Confirmation Page Open Tempo App Tapped', {
        email,
        inviteCode,
        ...extraProps,
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Subscription Invitation Confirmation Page Open Tempo App Tapped', {
        email,
        inviteCode,
        ...extraProps,
        validationId,
      });
    }
  };
  const trackSubscriptionInvitationFailurePageViewed = (email, inviteCode, errorMessage) => {
    let validationId = '';
    if (TempoEvents) {
      validationId = TempoEvents.track('Subscription Invitation Failure Page Viewed', {
        email,
        inviteCode,
        errorMessage: errorMessage || '',
      });
    }
    if (analyticsLoaded) {
      const analytics = _analytics();
      analytics.track('Subscription Invitation Failure Page Viewed', {
        email,
        inviteCode,
        errorMessage: errorMessage || '',
        validationId,
      });
    }
  };
  return {
    analyticsLoaded,
    trackRegisterPageViewed,
    trackRegisterPageSubmitTapped,
    trackRegisterSuccessfulPageViewed,
    trackRegisterSuccessfulPageOpenTempoAppTapped,
    trackExistingSubscriptionPageViewed,
    trackExistingSubscriptionPageContactSupportTapped,
    trackRegisterPaymentFailure,
    trackSubscriptionInvitationPageViewed,
    trackSubscriptionInvitationJoinFamilyTapped,
    trackSubscriptionInvitationConfirmationPageViewed,
    trackSubscriptionInvitationConfirmationPageOpenTempoAppTapped,
    trackSubscriptionInvitationFailurePageViewed,
    saveFormData,
    getFormData,
  };
};
